/**
 * @module permissions reducer
 *
 * Contains all data needed to know an authenticated user's authorized
 * permissions. Contains both global permissions (in the root) as well as a
 * dictionary of surveyType permissions, indexed by surveyType.id.
 *
 * @example permissions state
 * {
 *   canSeeSettings: true,
 *   canSeeEmployees: true,
 *   canSeeUsers: true,
 *   canSeeConversations: true,
 *   canSeeComments: true,
 *   canUpdateCommentSentiment: true,
 *   canSeeReporting: true,
 *   canSeeSurveys: true,
 *   canSeeActionPlanning: true,
 *   canManageContent: false,
 *   canDeleteCompanies: false,
 *   canUpdateCompanies: false,
 *
 *   surveyTypes: {
 *     1: {
 *       surveyTypeId: 1,
 *       companyId: 543,
 *       permissionId: 98,
 *       canSeeAll: true,
 *       canSeeReporting: true,
 *       canSeeConversations: true,
 *       canSeeComments: true,
 *       canUpdateCommentSentiment: true,
 *       canSeeHierarchy: true,
 *       canManageSurvey: true,
 *       canCompareToAll: true
 *     }
 *   }
 * }
 */
import { createReducer } from '@reduxjs/toolkit';
import { metaActions } from '../actions/meta.actions';
import { accountActions } from '../actions/account.actions';

const defaultState = {
  canSeeSettings: false,
  canSeeEmployees: false,
  canSeeUsers: false,
  canSeeConversations: false,
  canSeeComments: false,
  canUpdateCommentSentiment: false,
  canSeeReporting: false,
  canSeeSurveys: false,
  canSeeActionPlanning: false,
  canManageContent: false,
  canDeleteCompanies: false,
  canUpdateCompanies: false,
  canManageMailingList: false,
  surveyTypes: {},
};

const reset = () => defaultState;

// TODO: Add validation/filtering such that only defined permissions can be set
// and should be in the expected shape (e.g., for survey types)?
const mergePermissions = (state, action) => {
  const newPermissions = action?.payload?.permissions || {};

  return {
    ...state,
    ...newPermissions,
  };
};

const permissions = createReducer(defaultState, (builder) => {
  builder.addCase(metaActions.reset, reset);
  builder.addCase(accountActions.logout.pending, reset);
  builder.addCase(accountActions.fetchAccount.fulfilled, mergePermissions);
});

export default permissions;
